const LOCAL_PRESENTON_FASTAPI_ORIGIN = 'http://127.0.0.1:8000'
const LOCAL_PRESENTON_WEB_ORIGIN = 'http://127.0.0.1:3001'

function isTruthy(value) {
  return ['1', 'true', 'yes', 'on'].includes(String(value ?? '').trim().toLowerCase())
}

function isLoopbackHostname(hostname) {
  const normalized = hostname.toLowerCase().replace(/^\[|\]$/g, '')
  return (
    normalized === 'localhost' ||
    normalized === '::1' ||
    normalized === '0.0.0.0' ||
    normalized === '127.0.0.1' ||
    normalized.startsWith('127.')
  )
}

function resolveOrigin(environmentName, { environment, fallback, production, requireHttps }) {
  const configuredValue = environment[environmentName]?.trim()
  if (!configuredValue && !fallback) {
    throw new Error(`${environmentName} is required when PRESENTON_PROXY_ENABLED=true`)
  }

  let parsed
  try {
    parsed = new URL(configuredValue || fallback)
  } catch {
    throw new Error(`${environmentName} must be a valid HTTP(S) origin`)
  }

  const hasUnsupportedParts =
    parsed.username ||
    parsed.password ||
    (parsed.pathname !== '/' && parsed.pathname !== '') ||
    parsed.search ||
    parsed.hash
  if (!['http:', 'https:'].includes(parsed.protocol) || hasUnsupportedParts) {
    throw new Error(`${environmentName} must be an HTTP(S) origin without path or credentials`)
  }
  if (production && requireHttps && parsed.protocol !== 'https:') {
    throw new Error(`${environmentName} must use HTTPS in production proxy mode`)
  }
  if (production && isLoopbackHostname(parsed.hostname)) {
    throw new Error(`${environmentName} cannot use a loopback host in production proxy mode`)
  }

  return parsed.origin
}

function createRewrites(fastApiOrigin, webOrigin) {
  return [
    {
      source: '/admin/slides/api/v1/:path*',
      destination: `${fastApiOrigin}/api/v1/:path*`,
    },
    {
      source: '/admin/slides/api/v2/:path*',
      destination: `${fastApiOrigin}/api/v2/:path*`,
    },
    {
      source: '/admin/slides/app_data/:path*',
      destination: `${fastApiOrigin}/app_data/:path*`,
    },
    {
      source: '/admin/slides/static/:path*',
      destination: `${fastApiOrigin}/static/:path*`,
    },
    {
      source: '/admin/slides/:path*',
      destination: `${webOrigin}/admin/slides/:path*`,
    },
  ]
}

function createPresentonProxyConfig(environment = process.env) {
  const production = isTruthy(environment.PRESENTON_PROXY_ENABLED)
  const local = isTruthy(environment.PRESENTON_LOCAL_PROXY)

  if (production && local) {
    throw new Error('PRESENTON_PROXY_ENABLED and PRESENTON_LOCAL_PROXY cannot be enabled together')
  }
  if (!production && !local) return null

  const fastApiOrigin = resolveOrigin('PRESENTON_FASTAPI_ORIGIN', {
    environment,
    fallback: local ? LOCAL_PRESENTON_FASTAPI_ORIGIN : undefined,
    production,
    requireHttps: false,
  })
  const webOrigin = resolveOrigin('PRESENTON_WEB_ORIGIN', {
    environment,
    fallback: local ? LOCAL_PRESENTON_WEB_ORIGIN : undefined,
    production,
    requireHttps: true,
  })

  return {
    mode: production ? 'production' : 'local',
    rewrites: createRewrites(fastApiOrigin, webOrigin),
  }
}

function resolvePresentonCapabilityState(environment = process.env) {
  const capabilityEnabled = isTruthy(environment.PRESENTON_CAPABILITY_GENERATED_CONTENT)
  if (!capabilityEnabled) return false

  const productionProxy = isTruthy(environment.PRESENTON_PROXY_ENABLED)
  const localProxy = isTruthy(environment.PRESENTON_LOCAL_PROXY)

  if (!productionProxy && !localProxy) {
    throw new Error('PRESENTON_CAPABILITY_GENERATED_CONTENT=true requires PRESENTON_PROXY_ENABLED or PRESENTON_LOCAL_PROXY')
  }

  const privateKey = environment.PRESENTON_SIGNING_PRIVATE_KEY?.trim()
  const internalSecret = environment.PRESENTON_INTERNAL_SHARED_SECRET?.trim()

  if (!privateKey || !internalSecret) {
    throw new Error('PRESENTON_CAPABILITY_GENERATED_CONTENT=true requires PRESENTON_SIGNING_PRIVATE_KEY and PRESENTON_INTERNAL_SHARED_SECRET')
  }

  return true
}

module.exports = {
  LOCAL_PRESENTON_FASTAPI_ORIGIN,
  LOCAL_PRESENTON_WEB_ORIGIN,
  createPresentonProxyConfig,
  resolvePresentonCapabilityState,
}
